import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logoo.png";

export default function Navbar() {
  const loc = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState({ parish: false, religious: false });
  const [hasNewAnnouncement, setHasNewAnnouncement] = useState(false);
  const [scrollVelocity, setScrollVelocity] = useState(0);

  const mobileRef = useRef(null);
  const hamburgerRef = useRef(null);
  const lastScrollY = useRef(0);
  const lastScrollTime = useRef(Date.now());
  const ornamentRefs = useRef([]);

  // Check for new announcements
  useEffect(() => {
    const checkNewAnnouncement = () => {
      const lastRead = localStorage.getItem('lastReadAnnouncement');
      const latestAnnouncement = "2025-10-26T00:00:00Z";
      
      if (!lastRead || new Date(latestAnnouncement) > new Date(lastRead)) {
        setHasNewAnnouncement(true);
      } else {
        setHasNewAnnouncement(false);
      }
    };

    checkNewAnnouncement();
    const interval = setInterval(checkNewAnnouncement, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          const docHeight = document.documentElement.scrollHeight;
          const winHeight = window.innerHeight;
          const scrollPercent = (scrollTop / (docHeight - winHeight || 1)) * 100;

          // Calculate scroll velocity
          const currentTime = Date.now();
          const timeDiff = currentTime - lastScrollTime.current;
          const scrollDiff = scrollTop - lastScrollY.current;
          
          if (timeDiff > 0) {
            const velocity = Math.abs(scrollDiff / timeDiff) * 100; // pixels per second
            setScrollVelocity(velocity);
            
            // Apply physics to ornaments
            if (velocity > 5 && ornamentRefs.current.length > 0) {
              const swingAmount = Math.min(velocity * 2, 30); // Cap at 30 degrees
              const direction = scrollDiff > 0 ? 1 : -1;
              
              ornamentRefs.current.forEach((container, index) => {
                if (container) {
                  const randomFactor = 0.8 + Math.random() * 0.4; // 0.8 to 1.2
                  const swing = swingAmount * direction * randomFactor;
                  container.style.transform = `rotate(${swing}deg)`;
                  container.classList.add('swinging');
                  
                  // Reset after animation
                  setTimeout(() => {
                    container.style.transform = '';
                    container.classList.remove('swinging');
                  }, 500);
                }
              });
            }
          }
          
          lastScrollY.current = scrollTop;
          lastScrollTime.current = currentTime;

          setIsScrolled(scrollTop > 20);
          setShowNavbar(scrollPercent < 70);

          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useLayoutEffect(() => {
    const scrollTop = typeof window !== "undefined" ? window.scrollY : 0;
    setIsScrolled(scrollTop > 20);

    const navbarHeight = 135;
    try {
      document.body.style.paddingTop = `${navbarHeight}px`;
      document.body.style.overflowX = "hidden";
    } catch {
      // Ignore errors
    }

    return () => {
      try {
        document.body.style.paddingTop = "";
      } catch {
        // Ignore errors
      }
    };
  }, []);

  useEffect(() => {
    const navbarHeight = 135;
    try {
      document.body.style.paddingTop = `${navbarHeight}px`;
    } catch {
      // Ignore errors
    }
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.body.style.overflowX = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.overflowX = "hidden";
    };
  }, [mobileOpen]);

  const navItems = [
    { to: "/", label: "HOME" },
    { to: "/parish", label: "PARISH" },
    { to: "/history", label: "HISTORY" },
    { to: "/religious", label: "RELIGIOUS" },
    { to: "/Announcements", label: "ANNOUNCEMENTS" },
    { to: "/contact", label: "CONTACT" },
  ];

  const mobileNavItems = [
    { to: "/", label: "HOME", icon: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" },
    { 
      to: "/parish", 
      label: "PARISH", 
      icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
      hasDropdown: true,
      submenu: [
        { to: "/parish/clergy", label: "Clergy" },
        { to: "/parish/communities", label: "Communities" },
        { to: "/parish/cells", label: "Cells & Associations" },
        { to: "/parish/gallery", label: "Gallery" }
      ]
    },
    { to: "/history", label: "HISTORY", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
    { 
      to: "/religious", 
      label: "RELIGIOUS", 
      icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
      hasDropdown: true,
      submenu: [
        { to: "/religious/villa-theresa", label: "Villa Theresa High School" },
        { to: "/religious/sophia-college", label: "Sophia College" },
        { to: "/religious/st-elizabeth-hospital", label: "St. Elizabeth Hospital" }
      ]
    },
    { to: "/Announcements", label: "ANNOUNCEMENTS", icon: "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" },
    { to: "/contact", label: "CONTACT", icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
  ];

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 w-full"
        style={{
          zIndex: 60,
          transition:
            "all 0.5s cubic-bezier(0.2,0,0.2,1), transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
          background: isScrolled ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 1)",
          backdropFilter: isScrolled ? "blur(40px) saturate(200%)" : "blur(16px) saturate(180%)",
          WebkitBackdropFilter: isScrolled ? "blur(40px) saturate(200%)" : "blur(16px) saturate(180%)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.24), 0 4px 16px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.12)",
          height: "135px",
          borderBottom: isScrolled ? "1px solid rgba(255, 255, 255, 0.25)" : "none",
          transform: showNavbar ? "translateY(0)" : "translateY(-100%)",
          opacity: showNavbar ? 1 : 0,
          pointerEvents: showNavbar ? "auto" : "none",
        }}
      >
        <style>{`
          body { overflow-x: hidden; }

          

          .nav-link {
            position: relative;
            padding: 12px 20px;
            font-weight: 600;
            font-size: 0.875rem;
            letter-spacing: 0.02em;
            background: linear-gradient(135deg, #8B2635 0%, #6B1F2A 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            text-transform: uppercase;
            display: inline-flex;
            align-items: center;
            white-space: nowrap;
            border-radius: 8px;
            transition: all 0.45s cubic-bezier(0.2,0,0.2,1);
            cursor: pointer;
          }

          .nav-link:nth-child(1) { transition-delay: 0ms; }
          .nav-link:nth-child(2) { transition-delay: 30ms; }
          .nav-link:nth-child(3) { transition-delay: 60ms; }
          .nav-link:nth-child(4) { transition-delay: 90ms; }
          .nav-link:nth-child(5) { transition-delay: 120ms; }
          .nav-link:nth-child(6) { transition-delay: 150ms; }

          .nav-link:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            transform: translateY(-2px);
          }

          .nav-active {
            background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .underline {
            position: absolute;
            bottom: 4px;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%);
            transform: scaleX(0);
            transform-origin: center;
            transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 0 10px rgba(245, 158, 11, 0.6);
          }

          .nav-link:hover .underline { transform: scaleX(1); transition-delay: 0ms; }
          .nav-active .underline { transform: scaleX(1); }

          .notification-dot {
            position: absolute;
            top: 8px;
            right: 12px;
            width: 8px;
            height: 8px;
            background: #3b82f6;
            border-radius: 50%;
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8), 0 0 8px rgba(59, 130, 246, 0.6);
            animation: pulse-dot 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }

          @keyframes pulse-dot {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.1);
            }
          }

          .mobile-notification-dot {
            position: absolute;
            top: 14px;
            right: 16px;
            width: 8px;
            height: 8px;
            background: #3b82f6;
            border-radius: 50%;
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8), 0 0 8px rgba(59, 130, 246, 0.6);
            animation: pulse-dot 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }

          .nav-link:focus,
          .nav-link:focus-visible,
          .logo-container:focus,
          .logo-container:focus-visible,
          button:focus,
          button:focus-visible,
          a:focus,
          a:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }

          .logo-container { transition: transform 250ms ease; display: inline-flex; align-items: center; }
          .logo-container:hover { transform: scale(1.02); }
          .logo-img { transition: height 0.45s cubic-bezier(0.2,0,0.2,1); }

          .nav-item { position: relative; display: inline-flex; align-items: center; }
          .dropdown-toggle { display: inline-flex !important; align-items: center !important; }

          .dropdown-menu {
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%) translateY(-6px);
            min-width: 220px;
            background: #ffffff;
            border-radius: 12px;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
            padding: 8px;
            opacity: 0;
            pointer-events: none;
            visibility: hidden;
            transition:
              opacity 300ms ease,
              transform 300ms ease,
              visibility 0s linear 300ms;
            z-index: 999;
            border: 1px solid rgba(139, 38, 53, 0.1);
          }

          .nav-item:hover .dropdown-menu {
            opacity: 1;
            pointer-events: auto;
            transform: translateX(-50%) translateY(0);
            visibility: visible;
            transition:
              opacity 300ms ease,
              transform 300ms ease,
              visibility 0s linear 0s;
          }

          .dropdown-item {
            display: block;
            padding: 12px 16px;
            font-size: 0.95rem;
            font-weight: 500;
            color: #1f2937;
            text-decoration: none;
            white-space: nowrap;
            border-radius: 8px;
            transition: all 0.25s ease;
            background: transparent;
          }

          .dropdown-item:hover {
            background: rgba(139, 38, 53, 0.08);
            color: #8B2635; 
            transform: translateX(2px);
          }

          .dropdown-menu.right-align { left: auto; right: 0; }

          .mobile-menu-btn {
            display: none;
            position: absolute;
            right: 24px;
            top: 50%;
            transform: translateY(-50%);
          }

          @media (max-width: 1600px) {
            .navbar-inner { gap: clamp(45px, 4.5vw, 70px) !important; }
          }
          @media (max-width: 1400px) {
            .navbar-inner { gap: clamp(40px, 4vw, 60px) !important; }
          }
          @media (max-width: 1280px) {
            .navbar-inner { gap: clamp(35px, 3.5vw, 50px) !important; }
          }
          @media (max-width: 1024px) {
            .navbar-inner { gap: clamp(30px, 3vw, 40px) !important; }
          }

          @media (max-width: 768px) {
            .navbar-inner .nav-links {
              display: none !important;
            }
            .navbar-inner { gap: 20px; align-items: center; justify-content: center; }
            body { padding-top: 135px; }
            
            .mobile-menu-btn {
              display: block;
            }
          }

          .mobile-drawer-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0);
            backdrop-filter: blur(0px);
            opacity: 0;
            pointer-events: none;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            z-index: 999;
          }

          .mobile-drawer-overlay.open {
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(4px);
            opacity: 1;
            pointer-events: auto;
          }

          .mobile-drawer {
            position: fixed;
            top: 0;
            right: 0;
            height: 100vh;
            width: 85%;
            max-width: 400px;
            background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
            box-shadow: -8px 0 32px rgba(0, 0, 0, 0.12);
            transform: translateX(100%);
            opacity: 0;
            transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            z-index: 1000;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .mobile-drawer.open {
            transform: translateX(0);
            opacity: 1;
          }

          .mobile-drawer-header {
            padding: 24px 24px 20px;
            border-bottom: 1px solid rgba(139, 38, 53, 0.1);
            background: white;
            display: flex;
            align-items: center;
            justify-content: space-between;
            position: relative;
          }

          .mobile-drawer-header::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 24px;
            right: 24px;
            height: 2px;
            background: linear-gradient(90deg, #8B2635 0%, transparent 100%);
            opacity: 0.3;
          }

          .mobile-drawer-logo {
            display: flex;
            align-items: center;
            gap: 12px;
            font-weight: 700;
            font-size: 1.1rem;
            color: #1f2937;
            letter-spacing: -0.02em;
          }

          .mobile-close-btn {
            background: rgba(139, 38, 53, 0.08);
            border: none;
            width: 40px;
            height: 40px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .mobile-close-btn:hover {
            background: rgba(139, 38, 53, 0.15);
            transform: rotate(90deg);
          }

          .mobile-drawer-content {
            flex: 1;
            overflow-y: auto;
            padding: 12px 0;
            scrollbar-width: thin;
            scrollbar-color: rgba(139, 38, 53, 0.3) transparent;
          }

          .mobile-drawer-content::-webkit-scrollbar {
            width: 6px;
          }

          .mobile-drawer-content::-webkit-scrollbar-track {
            background: transparent;
          }

          .mobile-drawer-content::-webkit-scrollbar-thumb {
            background: rgba(139, 38, 53, 0.3);
            border-radius: 3px;
          }

          .mobile-menu-item {
            margin: 6px 16px;
            border-radius: 16px;
            overflow: hidden;
            background: white;
            border: 1px solid rgba(139, 38, 53, 0.08);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
          }

          .mobile-menu-item:hover {
            border-color: rgba(139, 38, 53, 0.2);
            box-shadow: 0 4px 12px rgba(139, 38, 53, 0.08);
            transform: translateX(-2px);
          }

          .mobile-menu-link {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 18px 20px;
            color: #374151;
            text-decoration: none;
            font-weight: 600;
            font-size: 0.95rem;
            letter-spacing: 0.02em;
            transition: all 0.3s ease;
            position: relative;
          }

          .mobile-menu-link::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 0;
            background: linear-gradient(135deg, #8B2635 0%, #6B1F2A 100%);
            transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .mobile-menu-link:hover::before {
            width: 4px;
          }

          .mobile-menu-item.active .mobile-menu-link {
            color: #8B2635;
            background: linear-gradient(90deg, rgba(139, 38, 53, 0.08) 0%, transparent 100%);
          }

          .mobile-menu-item.active .mobile-menu-link::before {
            width: 4px;
          }

          .mobile-menu-icon {
            width: 24px;
            height: 24px;
            flex-shrink: 0;
            transition: all 0.3s ease;
          }

          .mobile-menu-item.active .mobile-menu-icon {
            stroke: #8B2635;
          }

          .mobile-menu-item:hover .mobile-menu-icon {
            transform: scale(1.1);
          }

          .mobile-menu-button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding: 18px 20px;
            background: transparent;
            border: none;
            color: #374151;
            font-weight: 600;
            font-size: 0.95rem;
            letter-spacing: 0.02em;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
          }

          .mobile-menu-button::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 0;
            background: linear-gradient(135deg, #8B2635 0%, #6B1F2A 100%);
            transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .mobile-menu-button:hover::before {
            width: 4px;
          }

          .mobile-menu-item.active .mobile-menu-button {
            color: #8B2635;
            background: linear-gradient(90deg, rgba(139, 38, 53, 0.08) 0%, transparent 100%);
          }

          .mobile-menu-item.active .mobile-menu-button::before {
            width: 4px;
          }

          .mobile-menu-button-content {
            display: flex;
            align-items: center;
            gap: 16px;
          }

          .mobile-chevron {
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            flex-shrink: 0;
          }

          .mobile-chevron.open {
            transform: rotate(180deg);
          }

          .mobile-submenu {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            background: linear-gradient(180deg, rgba(139, 38, 53, 0.02) 0%, rgba(139, 38, 53, 0.04) 100%);
          }

          .mobile-submenu.open {
            max-height: 500px;
            border-top: 1px solid rgba(139, 38, 53, 0.1);
          }

          .mobile-subitem {
            display: block;
            padding: 14px 20px 14px 60px;
            color: #4b5563;
            text-decoration: none;
            font-size: 0.9rem;
            font-weight: 500;
            transition: all 0.2s ease;
            position: relative;
          }

          .mobile-subitem::before {
            content: '';
            position: absolute;
            left: 40px;
            top: 50%;
            transform: translateY(-50%);
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #9ca3af;
            transition: all 0.2s ease;
          }

          .mobile-subitem:hover {
            color: #8B2635;
            background: rgba(139, 38, 53, 0.05);
            padding-left: 65px;
          }

          .mobile-subitem:hover::before {
            background: #8B2635;
            transform: translateY(-50%) scale(1.3);
          }

          .mobile-subitem.active {
            color: #8B2635;
            background: rgba(139, 38, 53, 0.08);
            font-weight: 600;
          }

          .mobile-subitem.active::before {
            background: #8B2635;
            width: 8px;
            height: 8px;
          }

          .mobile-drawer-footer {
            padding: 20px 24px;
            border-top: 1px solid rgba(139, 38, 53, 0.1);
            background: white;
            text-align: center;
          }

          .mobile-drawer-footer-text {
            font-size: 0.8rem;
            color: #9ca3af;
            margin: 0;
          }

          .hamburger-icon {
            width: 28px;
            height: 28px;
            cursor: pointer;
            background: transparent;
            border: none;
            padding: 4px;
            border-radius: 8px;
            transition: background 0.2s ease;
          }

          .hamburger-icon:hover {
            background: rgba(139, 38, 53, 0.08);
          }

          .hamburger-line {
            fill: none;
            stroke: #8B2635;
            stroke-width: 2.5;
            stroke-linecap: round;
            transition: stroke-dasharray 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                        stroke-dashoffset 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                        transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .hamburger-line-1 {
            stroke-dasharray: 24 84;
            stroke-dashoffset: 0;
          }

          .hamburger-line-2 {
            stroke-dasharray: 24 84;
            stroke-dashoffset: 0;
          }

          .hamburger-line-3 {
            stroke-dasharray: 24 84;
            stroke-dashoffset: 0;
          }

          .hamburger-icon.open .hamburger-line-1 {
            stroke-dasharray: 17 84;
            stroke-dashoffset: -62px;
            transform: rotate(45deg);
            transform-origin: center;
          }

          .hamburger-icon.open .hamburger-line-2 {
            stroke-dasharray: 1 84;
            stroke-dashoffset: -13px;
          }

          .hamburger-icon.open .hamburger-line-3 {
            stroke-dasharray: 17 84;
            stroke-dashoffset: -62px;
            transform: rotate(-45deg);
            transform-origin: center;
          }

          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          .mobile-drawer.open .mobile-menu-item {
            animation: slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1) backwards;
          }

          .mobile-drawer.open .mobile-menu-item:nth-child(1) { animation-delay: 0.05s; }
          .mobile-drawer.open .mobile-menu-item:nth-child(2) { animation-delay: 0.1s; }
          .mobile-drawer.open .mobile-menu-item:nth-child(3) { animation-delay: 0.15s; }
          .mobile-drawer.open .mobile-menu-item:nth-child(4) { animation-delay: 0.2s; }
          .mobile-drawer.open .mobile-menu-item:nth-child(5) { animation-delay: 0.25s; }
          .mobile-drawer.open .mobile-menu-item:nth-child(6) { animation-delay: 0.3s; }
        `}</style>

        {/* Christmas Ornament Balls - Varied and Asymmetric */}
        <div 
          className="ornament-container ornament-left"
          ref={el => ornamentRefs.current[0] = el}
        >
          <div>
            <div className="ornament-string string-medium"></div>
            <div className="ornament-ball ball-large ball-red">
              <div className="ornament-cap"></div>
            </div>
          </div>
        </div>
        
        <div 
          className="ornament-container ornament-center-left"
          ref={el => ornamentRefs.current[1] = el}
        >
          <div>
            <div className="ornament-string string-short"></div>
            <div className="ornament-ball ball-small ball-gold">
              <div className="ornament-cap"></div>
            </div>
          </div>
        </div>

        <div 
          className="ornament-container ornament-center-right"
          ref={el => ornamentRefs.current[2] = el}
        >
          <div>
            <div className="ornament-string string-long"></div>
            <div className="ornament-ball ball-medium ball-green">
              <div className="ornament-cap"></div>
            </div>
          </div>
        </div>
        
        <div 
          className="ornament-container ornament-right"
          ref={el => ornamentRefs.current[3] = el}
        >
          <div>
            <div className="ornament-string string-medium"></div>
            <div className="ornament-ball ball-medium ball-silver">
              <div className="ornament-cap"></div>
            </div>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 60px",
          }}
        >
          <div
            className="navbar-inner"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "clamp(50px, 5vw, 80px)",
            }}
          >
            <Link to="/" className="logo-container" aria-label="St. Stephen's Church">
              <img
                src={logo}
                alt="St. Stephen's Church"
                className="logo-img"
                style={{
                  height: 65,
                  width: "auto",
                  display: "block",
                }}
              />
            </Link>

            <div className="nav-links" style={{ display: "flex", gap: "clamp(50px, 5vw, 80px)", alignItems: "center" }}>
              {navItems.map((item) => {
                const isActive =
                  loc.pathname === item.to || (item.to !== "/" && loc.pathname.startsWith(item.to));

                if (item.label === "PARISH") {
                  const isParishActive = loc.pathname.startsWith("/parish");
                  return (
                    <div key={item.to} className="nav-item" aria-haspopup="true" aria-expanded={isParishActive}>
                      <div
                        className={`nav-link dropdown-toggle ${isParishActive ? "nav-active" : ""}`}
                        role="button"
                        tabIndex={0}
                        style={{ display: "inline-flex", alignItems: "center" }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            window.location.pathname = "/parish";
                          }
                        }}
                      >
                        <span>PARISH</span>
                        <span className="underline" />
                      </div>

                      <div className="dropdown-menu">
                        <Link to="/parish/clergy" className="dropdown-item">Clergy</Link>
                        <Link to="/parish/communities" className="dropdown-item">Communities</Link>
                        <Link to="/parish/cells" className="dropdown-item">Cells & Associations</Link>
                        <Link to="/parish/gallery" className="dropdown-item">Gallery</Link>
                      </div>
                    </div>
                  );
                }

                if (item.label === "RELIGIOUS") {
                  const isRelActive = loc.pathname.startsWith("/religious");
                  return (
                    <div key={item.to} className="nav-item" aria-haspopup="true" aria-expanded={isRelActive}>
                      <div
                        className={`nav-link dropdown-toggle ${isRelActive ? "nav-active" : ""}`}
                        role="button"
                        tabIndex={0}
                        style={{ display: "inline-flex", alignItems: "center" }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            window.location.pathname = "/religious";
                          }
                        }}
                      >
                        <span>RELIGIOUS</span>
                        <span className="underline" />
                      </div>

                      <div className="dropdown-menu">
                        <Link to="/religious/villa-theresa" className="dropdown-item">Villa Theresa High School</Link>
                        <Link to="/religious/sophia-college" className="dropdown-item">Sophia College</Link>
                        <Link to="/religious/st-elizabeth-hospital" className="dropdown-item">St. Elizabeth Hospital</Link>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link key={item.to} to={item.to} className={`nav-link ${isActive ? "nav-active" : ""}`} aria-current={isActive ? "page" : undefined} style={{ position: 'relative' }}>
                    {item.label}
                    <span className="underline" />
                    {item.label === "ANNOUNCEMENTS" && hasNewAnnouncement && (
                      <span className="notification-dot"></span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="mobile-menu-btn" ref={hamburgerRef}>
            <button
              className={`hamburger-icon ${mobileOpen ? "open" : ""}`}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((p) => !p)}
            >
              <svg viewBox="0 0 24 24" width="28" height="28">
                <path className="hamburger-line hamburger-line-1" d="M4 7h16"/>
                <path className="hamburger-line hamburger-line-2" d="M4 12h16"/>
                <path className="hamburger-line hamburger-line-3" d="M4 17h16"/>
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      <div 
        className={`mobile-drawer-overlay ${mobileOpen ? "open" : ""}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Mobile Drawer */}
      <div ref={mobileRef} className={`mobile-drawer ${mobileOpen ? "open" : ""}`}>
        <div className="mobile-drawer-header">
          <div className="mobile-drawer-logo">
            <img
              src={logo}
              alt="St. Stephen's Church"
              style={{
                height: 40,
                width: "auto",
                display: "block",
              }}
            />
          </div>
          <button 
            className="mobile-close-btn"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B2635" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="mobile-drawer-content">
          {mobileNavItems.map((item) => {
            const isActive = loc.pathname === item.to || (item.to !== "/" && loc.pathname.startsWith(item.to));
            
            if (item.hasDropdown) {
              const isExpanded = expanded[item.label.toLowerCase()];
              return (
                <div key={item.to} className={`mobile-menu-item ${isActive ? "active" : ""}`}>
                  <button
                    className="mobile-menu-button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [item.label.toLowerCase()]: !prev[item.label.toLowerCase()] }))}
                  >
                    <div className="mobile-menu-button-content">
                      <svg className="mobile-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d={item.icon}/>
                      </svg>
                      <span>{item.label}</span>
                    </div>
                    <svg
                      className={`mobile-chevron ${isExpanded ? "open" : ""}`}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  <div className={`mobile-submenu ${isExpanded ? "open" : ""}`}>
                    {item.submenu.map((sub) => {
                      const isSubActive = loc.pathname === sub.to;
                      return (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          className={`mobile-subitem ${isSubActive ? "active" : ""}`}
                          onClick={() => setMobileOpen(false)}
                        >
                          {sub.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <div key={item.to} className={`mobile-menu-item ${isActive ? "active" : ""}`}>
                <Link
                  to={item.to}
                  className="mobile-menu-link"
                  onClick={() => setMobileOpen(false)}
                >
                  <svg className="mobile-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d={item.icon}/>
                  </svg>
                  <span>{item.label}</span>
                  {item.label === "ANNOUNCEMENTS" && hasNewAnnouncement && (
                    <span className="mobile-notification-dot"></span>
                  )}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}